import React, { useState, useEffect } from 'react';
import { 
  View, 
  Text, 
  TouchableOpacity, 
  TextInput, 
  Modal, 
  FlatList, 
  Alert,
  ActivityIndicator,
  Image,
  StyleSheet,
} from 'react-native';

import { Ionicons } from '@expo/vector-icons';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../convex/_generated/api';
import { useAuthData } from '../hooks/useAuthData';
import { colors, fontFamily } from '../constants/theme';

const BRONZE = '#9C6B42';

const passCardShadowStyle = {
  shadowColor: '#000',
  shadowOffset: { width: 0, height: 10 },
  shadowOpacity: 0.25,
  shadowRadius: 10,
  elevation: 15,
  backgroundColor: '#fafafa',
  borderTopColor: colors.gray200,
  borderLeftColor: colors.gray200,
  borderRightColor: colors.gray300,
  borderBottomColor: colors.gray400,
};

const GiftPassWorkflow = ({ 
  visible, 
  onClose, 
  pass, 
  selectedRestaurant,
  restaurantName 
}) => {
  const { authData } = useAuthData();
  const [currentStep, setCurrentStep] = useState(1); // 1: select recipient button, 2: recipient selection, 3: confirm, 4: success
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [giftResult, setGiftResult] = useState(null);

  // Queries
  const userDoc = useQuery(
    api.users.getUser,
    authData?.privyDid ? { privyDid: authData.privyDid } : "skip"
  );

  const searchResults = useQuery(
    api.friends.searchUsersByUsername,
    searchQuery.length >= 2 ? { 
      searchQuery, 
      currentUserPrivyDid: authData?.privyDid || '' 
    } : "skip"
  );

  // Mutations
  const giftPass = useMutation(api.passActions.giftPass);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (visible) {
      setCurrentStep(1);
      setSearchQuery('');
      setSelectedRecipient(null);
      setGiftResult(null);
    }
  }, [visible]);

  const handleClose = () => {
    setCurrentStep(1);
    setSearchQuery('');
    setSelectedRecipient(null);
    setGiftResult(null);
    onClose();
  };

  const handleSelectRecipientButton = () => {
    setCurrentStep(2);
  };

  const handleSelectRecipient = (user) => {
    setSelectedRecipient(user);
    setCurrentStep(3);
  };

  const handleGiftPass = async () => {
    if (!selectedRecipient || !pass || !userDoc?._id) {
      Alert.alert("Error", "Please select a recipient and pass.");
      return;
    }

    setIsLoading(true);
    try {
      const result = await giftPass({
        passId: pass._id,
        fromUserId: userDoc._id,
        toUserId: selectedRecipient._id,
      });

      if (result.success) {
        setGiftResult(result);
        setCurrentStep(4);
      } else {
        Alert.alert("Error", result.error || "Failed to gift pass");
      }
    } catch (error) {
      console.error("Error gifting pass:", error);
      Alert.alert("Error", "Failed to gift pass");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLetsDisgo = () => {
    handleClose();
  };

  const renderStep1 = () => (
    <View style={styles.step1Root}>
      {/* Header */}
      <View style={styles.mb1}>
        <View style={styles.headerRow}>
          <View style={styles.w6} />
          <Text style={styles.screenTitle}>Gift your pass!</Text>
          <TouchableOpacity onPress={handleClose}>
            <Ionicons name="close" size={24} color={BRONZE} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Description */}
      <Text style={styles.bodyDesc}>
        Earn +3 bonus loyalty points at this restaurant when your friend uses it!
      </Text>

      {/* Pass Details Card - Updated to match new design */}
      <View style={[styles.passCardOuter, passCardShadowStyle]}>
        <View style={styles.p4}>
          {/* Header Row */}
          <View style={styles.cardHeaderRow}>
            {/* Left Icon Area */}
            <View style={styles.iconLeft}>
              <Ionicons 
                name={pass?.icon && pass?.icon !== "🎁" ? pass.icon : "gift"} 
                size={30} 
                color={colors.disgoBrown} 
              />
            </View>
            
            {/* Right DISGO Pass Logo */}
            <View style={styles.logoWrap}>
              <Image 
                source={require('../assets/DisgoLogoBlack.png')}
                style={styles.logo64}
                resizeMode="contain"
              />
              <Text style={[styles.passBadge, { top: 42, left: 42 }]}>
                Pass
              </Text>
            </View>
          </View>
          
          {/* Main Content Area */}
          <View style={styles.cardBody}>
            {/* Main Offer Text */}
            <Text style={styles.offerTitle}>
              {pass?.title || "Unknown Offer"}
            </Text>
            
            {/* Restaurant Name */}
            <Text style={styles.restaurantName}>
              {restaurantName || selectedRestaurant?.name || "Unknown Restaurant"}
            </Text>
            
            {/* Day and Time */}
            {pass?.startTime && pass?.endTime && (
              <View style={styles.rowSm}>
                <Text style={styles.metaBold}>
                  {new Date(pass.startTime * 1000).toLocaleDateString('en-US', { weekday: 'long' })}
                </Text>
                <Text style={styles.metaRegular}>
                  • {new Date(pass.startTime * 1000).toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit',
                      hour12: true 
                  })}-{new Date(pass.endTime * 1000).toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit',
                      hour12: true 
                  })}
                </Text>
              </View>
            )}
            
            {/* Loyalty Tier */}
            {pass?.tierRequired && (
              <View style={styles.rowCenter}>
                <Text style={styles.metaBold}>
                  Loyalty Tiers •
                </Text>
                <Text style={[styles.metaRegular, { marginLeft: 4 }]}>
                  {pass.tierRequired}+
                </Text>
              </View>
            )}
          </View>
        </View>
      </View>

      {/* Select a recipient Button */}
      <TouchableOpacity 
        onPress={handleSelectRecipientButton}
        style={styles.primaryBtnRow}
      >
        <Text style={styles.primaryBtnLabel}>
          Select a recipient
        </Text>
        <Ionicons name="chevron-forward" size={20} color="white" />
      </TouchableOpacity>
    </View>
  );

  const renderStep2 = () => (
    <View style={styles.step2Root}>
      {/* Header */}
      <View style={styles.mb6}>
        <View style={styles.headerRow}>
          <TouchableOpacity onPress={() => setCurrentStep(1)}>
            <Ionicons name="chevron-back" size={24} color={BRONZE} />
          </TouchableOpacity>
          <Text style={styles.screenTitle}>Select Recipient</Text>
          <TouchableOpacity onPress={handleClose}>
            <Ionicons name="close" size={24} color={BRONZE} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Description */}
      <Text style={styles.bodyDesc}>
        Search for a friend to gift your pass to
      </Text>

      {/* Search Input */}
      <View style={styles.mb4}>
        <View style={styles.searchBox}>
          <Ionicons name="search" size={20} color="#666" />
          <TextInput
            style={styles.searchInput}
            placeholder="Search for friend to gift!"
            value={searchQuery}
            onChangeText={setSearchQuery}
            autoCapitalize="none"
            autoCorrect={false}
            placeholderTextColor={colors.gray500}
          />
        </View>
      </View>

      {/* Search Results */}
      {searchQuery.length >= 2 && (
        <View style={styles.flex1}>
          <FlatList
            data={searchResults || []}
            keyExtractor={(item) => item._id}
            renderItem={({ item }) => (
              <TouchableOpacity
                onPress={() => handleSelectRecipient(item)}
                style={styles.userRow}
              >
                <View style={styles.userRowInner}>
                  <View style={styles.avatarCircle}>
                    <Ionicons name="person" size={20} color="#666" />
                  </View>
                  <View style={styles.flex1}>
                    <Text style={styles.userName}>
                      {item.name || item.username}
                    </Text>
                    <Text style={styles.userHandle}>
                      @{item.username}
                    </Text>
                  </View>
                  <Ionicons name="chevron-forward" size={20} color="#666" />
                </View>
              </TouchableOpacity>
            )}
            showsVerticalScrollIndicator={false}
          />
        </View>
      )}

      {/* No results message */}
      {searchQuery.length >= 2 && (!searchResults || searchResults.length === 0) && (
        <View style={styles.centerFlex}>
          <Text style={styles.mutedCenter}>
            No friends found matching "{searchQuery}"
          </Text>
        </View>
      )}

      {/* Search prompt */}
      {searchQuery.length < 2 && (
        <View style={styles.centerFlex}>
          <Text style={styles.mutedCenter}>
            Type at least 2 characters to search for friends
          </Text>
        </View>
      )}
    </View>
  );

  const renderStep3 = () => (
    <View style={styles.step2Root}>
      {/* Header */}
      <View style={styles.mb6}>
        <View style={styles.headerRow}>
          <TouchableOpacity onPress={() => setCurrentStep(2)}>
            <Ionicons name="chevron-back" size={24} color={BRONZE} />
          </TouchableOpacity>
          <Text style={styles.screenTitle}>Gift your pass!</Text>
          <TouchableOpacity onPress={handleClose}>
            <Ionicons name="close" size={24} color={BRONZE} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Description */}
      <Text style={styles.bodyDesc}>
        Earn +3 bonus loyalty points at this restaurant when your friend uses it!
      </Text>

      {/* Pass Details Card - Updated to match new design */}
      <View style={[styles.passCardOuter, passCardShadowStyle]}>
        <View style={styles.p4}>
          {/* Header Row */}
          <View style={styles.cardHeaderRow}>
            {/* Left Icon Area */}
            <View style={styles.iconLeft}>
              <Ionicons 
                name={pass?.icon && pass?.icon !== "🎁" ? pass.icon : "gift"} 
                size={30} 
                color={colors.disgoBrown} 
              />
            </View>
            
            {/* Right DISGO Pass Logo */}
            <View style={styles.logoWrap}>
              <Image 
                source={require('../assets/DisgoLogoBlack.png')}
                style={styles.logo64}
                resizeMode="contain"
              />
              <Text style={[styles.passBadge, { top: 42, left: 42 }]}>
                Pass
              </Text>
            </View>
          </View>
          
          {/* Main Content Area */}
          <View style={styles.cardBody}>
            {/* Main Offer Text */}
            <Text style={styles.offerTitle}>
              {pass?.title || "Unknown Offer"}
            </Text>
            
            {/* Restaurant Name */}
            <Text style={styles.restaurantNameMd}>
              {restaurantName || selectedRestaurant?.name || "Unknown Restaurant"}
            </Text>
            
            {/* Day and Time */}
            {pass?.startTime && pass?.endTime && (
              <View style={styles.rowSm}>
                <Text style={styles.metaBoldSm}>
                  {new Date(pass.startTime * 1000).toLocaleDateString('en-US', { weekday: 'long' })}
                </Text>
                <Text style={styles.metaRegularSm}>
                  • {new Date(pass.startTime * 1000).toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit',
                      hour12: true 
                  })}-{new Date(pass.endTime * 1000).toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit',
                      hour12: true 
                  })}
                </Text>
              </View>
            )}
            
            {/* Loyalty Tier */}
            {pass?.tierRequired && (
              <View style={styles.rowCenter}>
                <Text style={styles.metaBoldSm}>
                  Loyalty Tiers •
                </Text>
                <Text style={[styles.metaRegularSm, { marginLeft: 4 }]}>
                  {pass.tierRequired}+
                </Text>
              </View>
            )}
          </View>
        </View>
      </View>

      {/* Selected Recipient */}
      <View style={styles.mb6}>
        <Text style={styles.labelSm}>Send to:</Text>
        <View style={styles.recipientRow}>
          <View style={styles.avatarSm}>
            <Ionicons name="person" size={16} color="#666" />
          </View>
          <Text style={styles.recipientName}>
            {selectedRecipient?.name || selectedRecipient?.username}
          </Text>
          <TouchableOpacity onPress={() => setCurrentStep(2)}>
            <Ionicons name="close" size={20} color="#666" />
          </TouchableOpacity>
        </View>
      </View>

      {/* Gift Pass Button */}
      <TouchableOpacity
        onPress={handleGiftPass}
        disabled={isLoading}
        style={styles.giftPassBtn}
      >
        {isLoading ? (
          <ActivityIndicator color="white" />
        ) : (
          <Text style={styles.giftPassBtnText}>Gift Pass</Text>
        )}
      </TouchableOpacity>
    </View>
  );

  const renderStep4 = () => (
    <View style={styles.step4Root}>
      {/* Header */}
      <View style={styles.mb6}>
        <View style={styles.headerRow}>
          <View style={styles.w6} />
          <Text style={styles.screenTitle}>Gift your pass!</Text>
          <TouchableOpacity onPress={handleClose}>
            <Ionicons name="close" size={24} color={BRONZE} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Description */}
      <Text style={styles.bodyDesc}>
        Earn +3 bonus loyalty points at this restaurant when your friend uses it!
      </Text>

      {/* Pass Details Card (faded) - Updated to match new design */}
      <View style={[styles.passCardOuter, passCardShadowStyle, styles.opacityHalf]}>
        <View style={styles.p4}>
          {/* Header Row */}
          <View style={styles.cardHeaderRow}>
            {/* Left Icon Area */}
            <View style={styles.iconLeft}>
              <Ionicons 
                name={pass?.icon && pass?.icon !== "🎁" ? pass.icon : "gift"} 
                size={30} 
                color={colors.disgoBrown} 
              />
            </View>
            
            {/* Right DISGO Pass Logo */}
            <View style={styles.logoWrap}>
              <Image 
                source={require('../assets/DisgoLogoBlack.png')}
                style={styles.logo80}
                resizeMode="contain"
              />
              <Text style={[styles.passBadge, { top: 42, left: 42 }]}>
                Pass
              </Text>
            </View>
          </View>
          
          {/* Main Content Area */}
          <View style={styles.cardBody}>
            {/* Main Offer Text */}
            <Text style={styles.offerTitle}>
              {pass?.title || "Unknown Offer"}
            </Text>
            
            {/* Restaurant Name */}
            <Text style={styles.restaurantNameMd}>
              {restaurantName || selectedRestaurant?.name || "Unknown Restaurant"}
            </Text>
            
            {/* Day and Time */}
            {pass?.startTime && pass?.endTime && (
              <View style={styles.rowSm}>
                <Text style={styles.metaBoldSm}>
                  {new Date(pass.startTime * 1000).toLocaleDateString('en-US', { weekday: 'long' })}
                </Text>
                <Text style={styles.metaRegularSm}>
                  • {new Date(pass.startTime * 1000).toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit',
                      hour12: true 
                  })}-{new Date(pass.endTime * 1000).toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit',
                      hour12: true 
                  })}
                </Text>
              </View>
            )}
            
            {/* Loyalty Tier */}
            {pass?.tierRequired && (
              <View style={styles.rowCenter}>
                <Text style={styles.metaBoldSm}>
                  Loyalty Tiers •
                </Text>
                <Text style={[styles.metaRegularSm, { marginLeft: 4 }]}>
                  {pass.tierRequired}+
                </Text>
              </View>
            )}
          </View>
        </View>
      </View>

      {/* Selected Recipient (faded) */}
      <View style={[styles.mb6, styles.opacityHalf]}>
        <Text style={styles.sendToLg}>send to:</Text>
        <View style={styles.recipientRow}>
          <View style={styles.avatarSm}>
            <Ionicons name="person" size={16} color="#666" />
          </View>
          <Text style={styles.recipientName}>
            {selectedRecipient?.name || selectedRecipient?.username}
          </Text>
        </View>
      </View>

      {/* Gift Pass Button (faded) */}
      <View style={styles.fadedGiftBtn}>
        <Text style={styles.fadedGiftBtnText}>gift pass</Text>
      </View>

      {/* Success Modal */}
      <View style={styles.successOverlay}>
        <View style={styles.successCard}>
          {/* Success Icon */}
          <View style={styles.successIcon}>
            <Ionicons name="checkmark" size={40} color="white" />
          </View>
          
          {/* Success Title */}
          <Text style={styles.successTitle}>
            Pass Gifted!
          </Text>
          
          {/* Points Earned */}
          <Text style={styles.successBody}>
            You earned <Text style={styles.successHighlight}>+{giftResult?.loyaltyPointsEarned || 3} bonus points</Text>!
          </Text>
          
          {/* Let's Disgo Button */}
          <TouchableOpacity
            onPress={handleLetsDisgo}
            style={styles.letsBtn}
          >
            <Text style={styles.letsBtnText}>LET'S DISGO!</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      default:
        return renderStep1();
    }
  };

  return (
    <Modal 
      visible={visible} 
      animationType="slide" 
      transparent={false}
      statusBarTranslucent={true}
    >
      <View style={styles.safe}>
        {renderCurrentStep()}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: colors.white },
  step1Root: { flex: 1, padding: 24, paddingTop: 144 },
  step2Root: { flex: 1, padding: 24, paddingTop: 112 },
  step4Root: { flex: 1, padding: 24, paddingTop: 112, position: 'relative' },
  mb1: { marginBottom: 4 },
  mb4: { marginBottom: 16 },
  mb6: { marginBottom: 24 },
  w6: { width: 24 },
  flex1: { flex: 1 },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  screenTitle: {
    fontSize: 24,
    fontFamily: fontFamily.abhayaBold,
    textAlign: 'center',
    color: colors.brownDark,
  },
  bodyDesc: {
    fontSize: 16,
    color: colors.gray600,
    marginBottom: 24,
    textAlign: 'center',
    marginHorizontal: 32,
    fontFamily: fontFamily.abhaya,
  },
  passCardOuter: {
    backgroundColor: colors.white,
    borderWidth: 2,
    borderColor: colors.gray300,
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 24,
  },
  p4: { padding: 16 },
  cardHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 0,
  },
  iconLeft: { alignItems: 'center', justifyContent: 'center', marginLeft: 16 },
  logoWrap: { alignItems: 'center', justifyContent: 'center', padding: 8, position: 'relative' },
  logo64: { width: 64, height: 64 },
  logo80: { width: 80, height: 80 },
  passBadge: {
    position: 'absolute',
    fontSize: 12,
    fontFamily: fontFamily.abhayaBold,
    color: colors.disgoBrown,
    textAlign: 'center',
  },
  cardBody: { marginLeft: 16, marginTop: -20, marginBottom: 8 },
  offerTitle: {
    fontSize: 20,
    fontFamily: fontFamily.abhayaBold,
    color: colors.brownDark,
    marginTop: 0,
  },
  restaurantName: {
    fontSize: 16,
    fontFamily: fontFamily.abhayaBold,
    color: colors.disgoBrown,
    marginBottom: 2,
  },
  restaurantNameMd: {
    fontSize: 16,
    fontFamily: fontFamily.abhayaBold,
    color: colors.disgoBrown,
    marginBottom: 2,
  },
  rowSm: { flexDirection: 'row', alignItems: 'center', marginBottom: 2 },
  rowCenter: { flexDirection: 'row', alignItems: 'center' },
  metaBold: {
    fontSize: 16,
    fontFamily: fontFamily.abhayaBold,
    color: colors.disgoBrown,
  },
  metaRegular: {
    fontSize: 16,
    fontFamily: fontFamily.abhaya,
    color: colors.disgoBrown,
    marginLeft: 4,
  },
  metaBoldSm: {
    fontSize: 14,
    fontFamily: fontFamily.abhayaBold,
    color: colors.disgoBrown,
  },
  metaRegularSm: {
    fontSize: 14,
    fontFamily: fontFamily.abhaya,
    color: colors.disgoBrown,
    marginLeft: 4,
  },
  primaryBtnRow: {
    backgroundColor: BRONZE,
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  primaryBtnLabel: {
    fontSize: 18,
    fontFamily: fontFamily.abhayaBold,
    marginRight: 8,
    color: colors.white,
  },
  searchBox: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.gray300,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  searchInput: {
    flex: 1,
    marginLeft: 12,
    fontSize: 16,
    color: colors.black,
    fontFamily: fontFamily.abhaya,
  },
  userRow: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.gray200,
    borderRadius: 8,
    padding: 16,
    marginBottom: 8,
  },
  userRowInner: { flexDirection: 'row', alignItems: 'center' },
  avatarCircle: {
    width: 40,
    height: 40,
    backgroundColor: colors.gray200,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  userName: { fontFamily: fontFamily.abhayaBold, fontSize: 16, color: colors.black },
  userHandle: { fontSize: 14, color: colors.gray500 },
  centerFlex: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  mutedCenter: { color: colors.gray500, textAlign: 'center', fontFamily: fontFamily.abhaya },
  labelSm: { fontSize: 14, fontFamily: fontFamily.abhayaBold, color: colors.gray700, marginBottom: 8 },
  recipientRow: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.gray300,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  avatarSm: {
    width: 32,
    height: 32,
    backgroundColor: colors.gray200,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  recipientName: { flex: 1, fontSize: 16, color: colors.black, fontFamily: fontFamily.abhaya },
  giftPassBtn: {
    backgroundColor: BRONZE,
    borderRadius: 8,
    paddingVertical: 16,
    paddingHorizontal: 24,
    alignItems: 'center',
  },
  giftPassBtnText: { color: colors.white, fontSize: 20, fontFamily: fontFamily.abhayaBold },
  opacityHalf: { opacity: 0.5 },
  sendToLg: { fontSize: 18, fontFamily: fontFamily.abhayaBold, color: colors.gray700, marginBottom: 8 },
  fadedGiftBtn: {
    backgroundColor: colors.gray300,
    borderRadius: 8,
    paddingVertical: 16,
    paddingHorizontal: 24,
    alignItems: 'center',
    marginBottom: 24,
  },
  fadedGiftBtnText: { color: colors.gray600, fontSize: 20, fontFamily: fontFamily.abhayaBold },
  successOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  successCard: {
    backgroundColor: colors.white,
    borderRadius: 24,
    padding: 32,
    marginHorizontal: 24,
    alignItems: 'center',
    maxWidth: 320,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.3,
    shadowRadius: 16,
    elevation: 20,
  },
  successIcon: {
    width: 80,
    height: 80,
    backgroundColor: BRONZE,
    borderRadius: 40,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  successTitle: {
    fontSize: 30,
    fontFamily: fontFamily.abhayaBold,
    color: colors.brownDark,
    marginBottom: 8,
    textAlign: 'center',
  },
  successBody: {
    fontSize: 18,
    fontFamily: fontFamily.abhaya,
    color: colors.disgoBrown,
    marginBottom: 24,
    textAlign: 'center',
  },
  successHighlight: { fontFamily: fontFamily.abhayaBold, color: BRONZE },
  letsBtn: {
    backgroundColor: colors.disgoBrown,
    borderRadius: 16,
    paddingVertical: 16,
    paddingHorizontal: 32,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 8,
  },
  letsBtnText: { color: colors.white, fontSize: 20, fontFamily: fontFamily.abhayaBold },
});

export default GiftPassWorkflow;
